<template>
  <div class="filter-menu">
    <div class="filter-menu-slider" :class="{ 'is-sub': activeCategory }">
      <!-- Panel 1: Category list -->
      <div class="filter-menu-categories">
        <div class="filter-menu-header">
          <span>Settings</span>
          <button v-if="hasActiveFilters" type="button" class="filter-menu-clear"
            @click="handleClear" v-tooltip="'Clear all'">
            <vue-feather type="x-circle" size="14" />
          </button>
        </div>
        <template v-for="(cat, idx) of categories" :key="cat.key">
          <div v-if="cat.section && idx > 0 && categories[idx - 1].section && categories[idx - 1].section !== cat.section"
            class="filter-menu-divider"></div>
          <div class="filter-menu-category" @click="openCategory(cat.key)">
            <vue-feather :type="cat.icon" size="15" class="filter-menu-category-icon" />
            <span class="filter-menu-category-label">{{ cat.label }}</span>
            <span class="filter-menu-category-value" :class="{ 'is-active': isCategoryActive(cat) }">{{ getActiveLabel(cat) }}</span>
            <vue-feather type="chevron-right" size="15" class="filter-menu-category-chevron" />
          </div>
        </template>
      </div>

      <!-- Panel 2: Options -->
      <div class="filter-menu-sub">
        <div class="filter-menu-back" @click="handleBack">
          <vue-feather type="chevron-left" size="15" />
          <span>{{ activeCategoryLabel }}</span>
        </div>
        <div class="filter-menu-sub-content" v-if="activeCat">
          <div class="fm-search" v-if="activeCat.searchable">
            <input v-model="searchQuery" placeholder="Search..."
              autocomplete="nope" data-lpignore="true" data-form-type="other" />
          </div>
          <div class="fm-list">
            <!-- Radio items -->
            <template v-if="activeCat.type === 'radio'">
              <div class="fm-item" v-for="opt of filteredOptions" :key="opt.value"
                :data-active="activeCat.value === opt.value" :data-disabled="!!opt.disabled"
                v-tooltip="opt.disabled && opt.tooltip ? opt.tooltip : ''" @click="selectOption(opt)">
                <div class="fm-radio" :class="{ checked: activeCat.value === opt.value }">
                  <div v-if="activeCat.value === opt.value" class="fm-radio-dot"></div>
                </div>
                <div v-if="opt.color" class="fm-tag-icon" :style="{ backgroundColor: opt.color }">
                  <vue-feather type="tag" size="12" />
                </div>
                <span>{{ opt.label }}</span>
                <span v-if="opt.count != null" class="fm-count">{{ opt.count }}</span>
              </div>
            </template>
            <!-- Checkbox items -->
            <template v-else-if="activeCat.type === 'checkbox'">
              <div class="fm-item" v-for="opt of filteredOptions" :key="opt.value"
                :data-active="activeCat.value.includes(opt.value)" :data-disabled="!!opt.disabled"
                v-tooltip="opt.disabled && opt.tooltip ? opt.tooltip : ''" @click="selectOption(opt)">
                <div class="fm-checkbox"
                  :class="{ checked: activeCat.value.includes(opt.value) }">
                  <vue-feather v-if="activeCat.value.includes(opt.value)" type="check" size="12" />
                </div>
                <div v-if="opt.color" class="fm-tag-icon" :style="{ backgroundColor: opt.color }">
                  <vue-feather type="tag" size="12" />
                </div>
                <span>{{ opt.label }}</span>
                <span v-if="opt.count != null" class="fm-count">{{ opt.count }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import { isCategoryActive, sameMembers } from '../../utils/listFilters.js'

export default {
  name: 'SidebarSettingsMenu',
  components: { 'vue-feather': VueFeather },
  props: {
    categories: {
      type: Array,
      required: true
    }
  },
  emits: ['select', 'clear', 'back'],
  data() {
    return {
      activeCategory: null,
      searchQuery: ''
    }
  },
  computed: {
    activeCat() {
      if (!this.activeCategory) return null
      return this.categories.find(c => c.key === this.activeCategory) || null
    },
    activeCategoryLabel() {
      return this.activeCat ? this.activeCat.label : ''
    },
    hasActiveFilters() {
      return this.categories.some(cat => this.isCategoryActive(cat))
    },
    filteredOptions() {
      if (!this.activeCat) return []
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.activeCat.options
      return this.activeCat.options.filter(o => o.label.toLowerCase().includes(q))
    }
  },
  methods: {
    // Shared with every list sidebar's filter-icon highlight and saved settings
    isCategoryActive,
    getActiveLabel(cat) {
      if (cat.type === 'radio') {
        if (cat.defaultValue !== undefined && cat.value === cat.defaultValue) return 'Default'
        const opt = cat.options.find(o => o.value === cat.value)
        return opt ? opt.label : 'Any'
      }
      if (cat.type === 'checkbox') {
        if (cat.defaultValue !== undefined) {
          // A one-option default (a Provider filter pinned to the user) is named below rather than "Default"
          if (sameMembers(cat.value, cat.defaultValue) && cat.value.length !== 1) return 'Default'
          if (cat.value.length === 0) return 'None'
          if (cat.value.length === 1) {
            const opt = cat.options.find(o => o.value === cat.value[0])
            return opt ? opt.label : '1 selected'
          }
          return `${cat.value.length} of ${cat.options.length}`
        }
        if (!cat.value || cat.value.length === 0) return 'Any'
        if (cat.value.length === 1) {
          const opt = cat.options.find(o => o.value === cat.value[0])
          return opt ? opt.label : '1 selected'
        }
        return `${cat.value.length} selected`
      }
      return ''
    },
    // A disabled option (`disabled`, with an optional `tooltip` saying why) can't be picked
    selectOption(opt) {
      if (opt.disabled) return
      this.$emit('select', this.activeCat.key, opt.value)
    },
    openCategory(key) {
      this.activeCategory = key
      this.searchQuery = ''
    },
    handleBack() {
      this.activeCategory = null
      this.searchQuery = ''
      this.$emit('back')
    },
    handleClear() {
      this.activeCategory = null
      this.searchQuery = ''
      this.$emit('clear')
    }
  }
}
</script>

<style scoped>
.filter-menu {
  overflow: hidden;
  width: 100%;
}

.filter-menu-slider {
  display: flex;
  width: 200%;
  transition: transform 0.2s ease;
}

.filter-menu-slider.is-sub {
  transform: translateX(-50%);
}

.filter-menu-categories,
.filter-menu-sub {
  width: 50%;
  min-width: 0;
}

/* Header */
.filter-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f3f4f6;
}

.filter-menu-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 2px;
  border-radius: 4px;
  transition: all 0.12s ease;
}

.filter-menu-clear:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
}

/* Section divider */
.filter-menu-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 4px 14px;
}

/* Category rows */
.filter-menu-category {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.1s ease;
}

.filter-menu-category:hover {
  background: #f3f4f6;
}

.filter-menu-category-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.filter-menu-category-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

/* Takes all the room beside the label, so a value only cuts off once the row runs out of space */
.filter-menu-category-value {
  flex: 1 1 0;
  text-align: right;
  font-size: 0.75rem;
  font-weight: 500;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-menu-category-value.is-active {
  color: var(--color-primary, #6366f1);
  font-weight: 600;
}

.filter-menu-category-chevron {
  color: #d1d5db;
  flex-shrink: 0;
}

/* Back header */
.filter-menu-back {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s ease;
}

.filter-menu-back:hover {
  background: #f9fafb;
}

/* Sub-content */
.filter-menu-sub-content {
  max-height: 300px;
  overflow-y: auto;
}

/* Search */
.fm-search {
  position: sticky;
  top: 0;
  background: #FFF;
  padding: 8px 8px 6px;
  z-index: 1;
}

.fm-search input {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.8125rem;
  outline: none;
  box-sizing: border-box;
}

.fm-search input:focus {
  border-color: var(--color-primary);
}

/* Option list */
.fm-list {
  padding: 4px 6px 6px;
}

/* Option item */
.fm-item {
  padding: 8px 10px;
  gap: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  transition: background 0.1s ease;
}

.fm-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fm-item:hover {
  background: #f3f4f6;
}

.fm-item[data-active="true"] {
  background: #e9edff;
}

/* Same look as DropdownInput's dropdown-item--disabled */
.fm-item[data-disabled="true"] {
  opacity: 0.5;
  cursor: not-allowed;
}

.fm-item[data-disabled="true"]:not([data-active="true"]):hover {
  background: transparent;
}

/* Radio indicator */
.fm-radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.12s ease;
}

.fm-radio.checked {
  border-color: var(--color-primary, #6366f1);
}

.fm-radio-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-primary, #6366f1);
}

/* Checkbox indicator */
.fm-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #FFF;
  transition: all 0.12s ease;
}

.fm-checkbox.checked {
  background: var(--color-primary, #6366f1);
  border-color: var(--color-primary, #6366f1);
}

/* Count badge */
.fm-count {
  margin-left: auto;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 1px 7px;
  border-radius: 10px;
  flex-shrink: 0;
}

/* Tag icon circle — matches fax details panel tag-icon-circle */
.fm-tag-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #FFF;
}
</style>
